
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { File, UploadCloud } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";

export default function Upload() {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { toast } = useToast();

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/pdf': ['.pdf'],
    },
    maxFiles: 1,
    onDrop: async (acceptedFiles) => {
      if (acceptedFiles && acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        handleUpload(file);
      }
    },
  });

  const handleUpload = async (file: File) => {
    setIsUploading(true);
    setUploadProgress(0);
    setError(null);

    try {
      // Get authenticated user
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error("You must be logged in to upload templates");
      }
      
      const userId = session.user.id;
      setUploadProgress(20);
      
      // Generate a unique filename for storage with user folder structure
      const timestamp = Date.now();
      const fileExtension = file.name.split('.').pop();
      const sanitizedFilename = file.name.replace(/\.[^/.]+$/, "").replace(/[^a-z0-9]/gi, '_').toLowerCase();
      const storagePath = `${userId}/${sanitizedFilename}_${timestamp}.${fileExtension}`;
      
      // Upload the file to Supabase Storage
      console.log("Uploading file to storage...");
      const { data: uploadData, error: uploadError } = await supabase
        .storage
        .from('templates')
        .upload(storagePath, file);
      
      if (uploadError) {
        throw uploadError;
      }
      
      setUploadProgress(70);
      
      // Create template record with pending processing status
      console.log("Creating template record...");
      const { data: templateData, error: dbError } = await (supabase as any)
        .from('templates')
        .insert({
          user_id: userId,
          filename: file.name,
          file_path: storagePath,
          size: file.size,
          content_type: file.type,
          processing_status: 'pending',
          needs_review: false,
          is_approved: true
        })
        .select()
        .single();

      if (dbError) {
        // Cleanup the uploaded file if database insert fails
        await supabase.storage
          .from('templates')
          .remove([storagePath]);
        throw dbError;
      }
      
      setUploadProgress(90);

      // Trigger background processing
      console.log("Triggering background processing for template:", templateData.id);
      try {
        const { data: functionResult, error: functionError } = await supabase.functions.invoke('process-template-async', {
          body: { templateId: templateData.id }
        });

        if (functionError) {
          console.error('Function invocation error:', functionError);
          // Don't fail the upload, just log the error - processing can be retried
        } else {
          console.log('Background processing started:', functionResult);
        }
      } catch (error) {
        console.error('Error invoking background processing:', error);
        // Don't fail the upload, processing can be retried
      }
      
      setUploadProgress(100);
      
      toast({
        title: "Upload successful",
        description: "Your template is being processed. You'll be able to review placeholders shortly.",
      });

      // Redirect to placeholder review page
      setTimeout(() => {
        navigate(`/templates/${templateData.id}/review-placeholders`);
      }, 2000);
      
    } catch (error) {
      console.error("Upload error:", error);
      setError(error instanceof Error ? error.message : String(error));
      toast({
        title: "Upload failed",
        description: error instanceof Error ? error.message : "There was an error uploading your template",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="container mx-auto p-8">
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">Upload Template</CardTitle>
          <CardDescription>
            Upload a DOCX document to create a new template. The document will be processed automatically in the background after upload.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div
            {...getRootProps()}
            className={`relative border-2 border-dashed rounded-md p-6 text-center cursor-pointer ${
              isUploading ? "pointer-events-none opacity-60" : ""
            } ${error ? "border-red-300" : ""}`}
          >
            <input {...getInputProps()} />
            <UploadCloud className="h-10 w-10 mx-auto text-gray-400 mb-2" />
            <p className="text-gray-500">
              {isUploading
                ? `Uploading... ${uploadProgress}%`
                : "Drag 'n' drop a file here, or click to select a file"}
            </p>
            <p className="text-sm text-gray-400 mt-1">
              Supported formats: .docx, .pdf
            </p>
            {isUploading && (
              <progress value={uploadProgress} max="100" className="w-full mt-4"></progress>
            )}
          </div>

          {error && (
            <Alert variant="destructive" className="mt-4">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Upload Failed</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
